import { useContext } from "react";
import { isNil } from "lodash";
import { useTranslation } from "react-i18next";
import { useModal } from "react-modal-hook";
import { useLocation } from "react-router";
import { RESOURCE_NAME } from "common/authorization";
import {
  FEATURE_FLAG_NAME,
  OBJECT_TYPE,
  SUBMISSION_CATEGORY,
  SUBMISSION_TYPE_MODULE,
} from "common/constants";
import { buildLink, INFO_PANEL_TAB_NAME } from "common/routing";
import {
  AccountDocumentTemplate,
  AiAction,
  CustomMapGeometry,
  Property,
  Submission,
  SubmissionStatus,
  SubmissionType,
} from "../../generated/graphql";
import { SUBMISSION_CATEGORY_TO_TAB } from "../../utils/submissions";
import { track } from "../../utils/tracking";
import DeleteConfirmation from "../AddressPanel/Submissions/DeleteConfirmation";
import EditVisibility from "../AddressPanel/Submissions/EditVisibility";
import { AuthContext } from "../Authorization/AuthContext";
import { Actions } from "../Common/ActionCell";
import Modal from "../Common/Modal";
import {
  ActionsProps,
  shouldShowViewOnMapActionForCustomMapGeometry,
  shouldShowViewOnMapActionForProperty,
} from "../Inputs/DropdownMenu";
import { useCreateTaskModal } from "../Tasks/Form/CreateTaskForm";
import GenerateDocument from "./GenerateDocument";
import RunAIAction from "./RunAIAction";

export type SubmissionActionButtonProps = {
  isEditView?: boolean;
  isSummaryView?: boolean;
  submission: Pick<Submission, "id" | "status"> & {
    aiActions?: Array<AiAction>;
    property?: Maybe<
      Pick<Property, "id" | "fullAddress" | "longitude" | "latitude">
    >;
    documentTemplates?: Array<Pick<AccountDocumentTemplate, "id" | "name">>;
    customMapGeometry?: Maybe<
      Pick<CustomMapGeometry, "id" | "customMapId"> & {
        label?: string;
      }
    >;
    submissionTypeVersion: {
      submissionType: Pick<SubmissionType, "id" | "category" | "name"> & {
        modules: Array<SUBMISSION_TYPE_MODULE>;
        aiActions?: Array<AiAction>;
      };
    };
    hasSummary: boolean;
    hiddenFromPublic?: boolean;
    relatedSubmissions: Array<
      Pick<Submission, "id"> & {
        submissionTypeVersion: {
          submissionType: Pick<SubmissionType, "modules" | "name">;
        };
      }
    >;
    workflowStep?: Maybe<{
      id: string;
      workflow?: {
        id: string;
      };
    }>;
    category: SUBMISSION_CATEGORY;
  };
  onUpdate?: () => void;
  additionalActions?: Array<ActionsProps>;
  objectIsHiddenFromPublic?: boolean;
};

const getSubmissionTypeName = ({
  modules,
  name,
}: Pick<SubmissionType, "name" | "modules">) => {
  if (modules.includes(SUBMISSION_TYPE_MODULE.SUBSTANTIAL_DAMAGE_ESTIMATE)) {
    return "SDE";
  } else if (
    modules.includes(
      SUBMISSION_TYPE_MODULE.SUBSTANTIAL_IMPROVEMENT_SUBSTANTIAL_DAMAGE,
    )
  ) {
    return "SD";
  } else {
    return name;
  }
};

const ActionButton = ({
  isEditView = false,
  isSummaryView = false,
  submission,
  onUpdate,
  additionalActions = [],
  objectIsHiddenFromPublic,
}: SubmissionActionButtonProps) => {
  const { pathname, search, state } = useLocation<{
    prevLocation?: Maybe<string>;
  }>();
  const { t } = useTranslation();
  const { authorized, account, isPublic, publicUser } = useContext(AuthContext);
  const isGuest = isPublic && !publicUser;

  const canRunAiActions = authorized({
    permission: "run",
    resource: RESOURCE_NAME.AI_ACTION,
  });

  const canCreateTask = authorized({
    permission: "create",
    resource: RESOURCE_NAME.TASK,
  });

  const isDetailView = isSummaryView || isEditView;
  const actionPrevLocation =
    isDetailView ? (state?.prevLocation ?? "") : undefined;

  const [showDeleteSubmissionModal, hideDeleteSubmissionModal] = useModal(
    () => (
      <Modal onRequestClose={hideDeleteSubmissionModal}>
        <DeleteConfirmation
          submission={submission}
          prevLocation={actionPrevLocation}
          closeModal={hideDeleteSubmissionModal}
          onUpdate={onUpdate}
        />
      </Modal>
    ),
  );

  const [showEditVisibilityModal, hideEditVisibilityModal] = useModal(
    () => (
      <Modal onRequestClose={hideEditVisibilityModal}>
        <EditVisibility
          submission={{
            ...submission,
            hiddenFromPublic: submission.hiddenFromPublic!,
          }}
          prevLocation={actionPrevLocation}
          closeModal={hideEditVisibilityModal}
          onUpdate={onUpdate}
          submissionTypeName={
            submission.submissionTypeVersion.submissionType.name
          }
        />
      </Modal>
    ),
    [submission.hiddenFromPublic],
  );

  const aiActions = submission.aiActions;
  const [showRunAIActionModal, hideRunAIActionModal] = useModal(
    () => (
      <Modal onRequestClose={hideRunAIActionModal}>
        <RunAIAction
          submission={submission}
          prevLocation={actionPrevLocation}
          closeModal={hideRunAIActionModal}
        />
      </Modal>
    ),
    aiActions,
  );

  const documentTemplates = submission.documentTemplates;
  const [showGenerateDocumentModal, hideGenerateDocumentModal] = useModal(
    () => (
      <Modal onRequestClose={hideGenerateDocumentModal}>
        <GenerateDocument
          submission={submission}
          object={{
            id: targetObject?.id!,
            objectType: targetObjectType,
          }}
          subtitle={targetObjectLabel ?? ""}
          prevLocation={actionPrevLocation}
          closeModal={hideGenerateDocumentModal}
        />
      </Modal>
    ),
    documentTemplates,
  );

  const prevLocation = `${pathname}${search}`;
  const recordTab = SUBMISSION_CATEGORY_TO_TAB[submission.category];
  const hasSummary = submission.hasSummary;

  const canDeleteSubmission =
    authorized({
      resource: RESOURCE_NAME.SUBMISSION,
      permission: "delete",
    }) &&
    submission.status !== SubmissionStatus.PROCESSING &&
    !submission.workflowStep;

  const canUpdateVisibility = authorized({
    resource: RESOURCE_NAME.SUBMISSION,
    permission: "updateVisibility",
  });

  const actions: Array<ActionsProps> = [];

  if (!isDetailView) {
    actions.push({
      href: buildLink("viewSubmission", { submissionId: submission.id }, {}),
      label: t("property-record-view"),
      target: "_blank",
      track: () => {
        track("Submission viewed", {
          submissionId: submission.id,
          "Submission Category":
            submission.submissionTypeVersion.submissionType.category,
          "Submission Type":
            submission.submissionTypeVersion.submissionType.name,
        });
      },
    });
  }

  if (isSummaryView && !isPublic) {
    actions.push({
      label: "Edit",
      to: {
        pathname: buildLink("editSubmission", {
          submissionId: submission.id,
        }),
        state: { prevLocation },
      },
    });
  }

  if (isEditView && hasSummary && !isPublic) {
    actions.push({
      label: "View summary",
      href: buildLink("submissionSummary", { submissionId: submission.id }, {}),
      target: "_blank",
    });
  }

  const relatedSubmissions = submission.relatedSubmissions;
  if (relatedSubmissions.length && !isPublic) {
    actions.push(
      ...relatedSubmissions.map(relatedSubmission => {
        return {
          label: `View ${getSubmissionTypeName(
            relatedSubmission.submissionTypeVersion.submissionType,
          )} record`,
          href: buildLink(
            "viewSubmission",
            {
              submissionId: relatedSubmission.id,
            },
            {},
          ),
          target: "_blank",
        };
      }),
    );
  }

  const workflowStep = submission.workflowStep;
  if (workflowStep?.workflow && !isGuest) {
    actions.push({
      label: "View related workflow",
      href: buildLink("workflow", { workflowId: workflowStep.workflow.id }, {}),
      target: "_blank",
    });
  }

  const property = submission.property;
  const customMapGeometry = submission.customMapGeometry;
  const targetObject = property || customMapGeometry;
  const targetObjectLabel = property?.fullAddress || customMapGeometry?.label;
  const targetObjectType =
    property ? OBJECT_TYPE.PROPERTY : OBJECT_TYPE.CUSTOM_MAP_GEOMETRY;
  if (!isPublic) {
    if (shouldShowViewOnMapActionForProperty(property)) {
      actions.push({
        label: "View on map",
        href: buildLink(
          "map",
          { accountId: account?.id! },
          {
            address: property.fullAddress,
            propertyId: property.id,
            lat: property.latitude.toString(),
            lng: property.longitude.toString(),
            tab: INFO_PANEL_TAB_NAME.RECORDS,
            recordTab,
          },
        ),
        target: "_blank",
      });
    } else if (
      shouldShowViewOnMapActionForCustomMapGeometry(customMapGeometry)
    ) {
      actions.push({
        label: "View on map",
        href: buildLink(
          "map",
          { accountId: account?.id! },
          {
            geometryId: customMapGeometry.id,
            customMapId: customMapGeometry.customMapId,
            tab: INFO_PANEL_TAB_NAME.RECORDS,
            recordTab,
          },
        ),
        target: "_blank",
      });
    }
  }

  if (isSummaryView || isEditView) {
    actions.push({
      label: "Print",
      onClick: () => {
        window.print();
      },
    });
  }

  if (aiActions && aiActions.length > 0 && canRunAiActions) {
    actions.push({
      label: "AI Actions",
      onClick: () => {
        showRunAIActionModal();
      },
    });
  }

  if (targetObject && documentTemplates && documentTemplates.length > 0) {
    actions.push({
      label: "Generate a file",
      onClick: () => {
        showGenerateDocumentModal();
      },
    });
  }

  actions.push(...additionalActions);

  let shouldShowVisibilityAction;
  if (
    // Tables aren't guaranteed to return hiddenFromPublic
    isNil(submission.hiddenFromPublic) ||
    !account?.publicPortal.enabled ||
    isPublic ||
    objectIsHiddenFromPublic
  ) {
    shouldShowVisibilityAction = false;
  } else if (!account.publicPortal.hideSISD) {
    shouldShowVisibilityAction = true;
  } else if (
    !submission.submissionTypeVersion.submissionType.category ||
    submission.submissionTypeVersion.submissionType.category ===
      SUBMISSION_CATEGORY.SUBSTANTIAL_IMPROVEMENT_SUBSTANTIAL_DAMAGE
  ) {
    shouldShowVisibilityAction = false;
  } else {
    shouldShowVisibilityAction = true;
  }

  const [showTaskModal] = useCreateTaskModal({
    targetObject:
      targetObject ?
        { ...targetObject, label: targetObjectLabel ?? "" }
      : undefined,
    submission,
  });

  const tasksFeature = account?.featureFlags.find(
    ff => ff.feature === FEATURE_FLAG_NAME.TASKS_WEB,
  );

  if (tasksFeature?.isEnabled && canCreateTask) {
    actions.push({
      label: "Add task",
      onClick: () => showTaskModal(),
    });
  }

  if (shouldShowVisibilityAction) {
    actions.push({
      label:
        submission.hiddenFromPublic ?
          "Show on public website"
        : "Hide on public website",
      onClick: () => showEditVisibilityModal(),
      disabled: !canUpdateVisibility,
      track: () => {
        track("Submission visibility updated", {
          submissionId: submission.id,
          "Submission Category":
            submission.submissionTypeVersion.submissionType.category,
          "Submission Type":
            submission.submissionTypeVersion.submissionType.name,
        });
      },
    });
  }

  if (!isPublic) {
    actions.push({
      label: "Delete",
      onClick: () => showDeleteSubmissionModal(),
      disabled: !canDeleteSubmission,
      variant: "red",
      track: () => {
        track("Submission deleted", {
          submissionId: submission.id,
          "Submission Category":
            submission.submissionTypeVersion.submissionType.category,
          "Submission Type":
            submission.submissionTypeVersion.submissionType.name,
        });
      },
    });
  }

  return actions.length ?
      <Actions
        actions={actions}
        buttonText={isDetailView ? "Actions" : undefined}
      />
    : <></>;
};

export default ActionButton;
